import React, { useState, useEffect, Fragment } from "react";
import { useTable, useBlockLayout } from "react-table";
import { formatValue } from "./Utils";
import dayjs from "dayjs";
import Tooltip from "./form-components/Tooltip.jsx";


const OrganisationCostOverviewTable = (props) => {
	const { sourceData, reportingPeriods,  organisationLevel1, organisationLevel2 } = props;

	const [data, setData] = useState([]);


	const headerClass = "th uppercase text-sm flex-row flex justify-end mr-3";
    
    useEffect(() => {
        if (sourceData !== undefined && sourceData.length > 0) {
            setData(sourceData);
        } else {
            setData([]);
        }
    }, [sourceData]);

	const descriptionCell = (cell) => {

        const organisationLevel = cell.row.original.organisation === "Total" ? "Total" : cell.row.original.sub_organisation === "" ? "Organisation" : "Sub organisation";
        
		return (
			<div className={`mt-2 ${organisationLevel === "Total" || organisationLevel === "Organisation" ? 'font-semibold' : 'font-normal'} text-text-2 flex-row flex`}>
				<div className="flex-row flex items-center">
					{cell.value}
				</div>
			</div>
		)
	}

	const valueCell = (cell) => {

        const organisationLevel = cell.row.original.organisation === "Total" ? "Total" : cell.row.original.sub_organisation === "" ? "Organisation" : "Sub organisation";

		return (
			<div className={`mt-2 ${organisationLevel === "Total" || organisationLevel === "Organisation" ? 'font-semibold' : 'font-normal'} text-text-2 flex-row flex justify-end`}>
				<div className="flex-row flex items-center">
					{formatValue(cell.value, "currency", false)}
				</div>
			</div>
		)
	}

	const valueHeader = (header) => {
		const periodNumber = header.column.id.split("C")[1];

		return (
			<div className={headerClass}>
				<span data-tooltip-id="instantTooltip" data-tooltip-content="The date that the cost was incurred">
					{dayjs(reportingPeriods[0]).add(periodNumber - 1, "month").format("MMM YYYY")}
				</span>
			</div>
		)
	}

	const columnDefs = [
		{
			Header: (cell) => (
				<div>
					<span data-tooltip-id="instantTooltip" data-tooltip-content="The name of the organisation at the top level">
						{organisationLevel1}
					</span>
				</div>
			), 
			accessor: "organisation",
			Cell: (cell) => {
                const organisationLevel = cell.row.original.organisation === "Total" ? "Total" : cell.row.original.sub_organisation === "" ? "Organisation" : "Sub organisation";
                return organisationLevel === "Total" || organisationLevel === "Organisation" ? descriptionCell(cell) : "";
            },
            width: 300,
		},
		{
			Header: (cell) => (
				<div>
					<span data-tooltip-id="instantTooltip" data-tooltip-content="The name of the organisation at the second level">
						{organisationLevel2}
					</span>
				</div>
			),
			accessor: "sub_organisation",
			Cell: (row) => descriptionCell(row),
            width: 300,
		},
		{
			Header: (cell) => (
				<div>
					<span data-tooltip-id="instantTooltip" data-tooltip-content="The total cost">
						Total
					</span>
				</div>
			),
			accessor: "total",
			Cell: (cell) => valueCell(cell),
            width: 125,
		}
	];

	function addColumns(reportingPeriods) {
		for (let i = 0; i < reportingPeriods.length; i++) {
			columnDefs.push({
				accessor: `C${i + 1}`,
				Header: (header) => valueHeader(header),
				Cell: (cell) => valueCell(cell),
    	        width: 125,
			});
		}
	}

	addColumns(reportingPeriods);

	return (
		<Fragment>
			<div className="h-64 ml-10 font-semibold text-3xl text-text-2"></div>
			<Tooltip />
			<Table columns={columnDefs} data={data} />
		</Fragment>
	);    
};

export default OrganisationCostOverviewTable;

function Table({ columns, data }) {
	const defaultColumn = React.useMemo(() => ({ width: "auto" }), []);

	const {
		getTableProps,
		getTableBodyProps,
		headerGroups,
		rows,
		prepareRow,
		initialState,
	} = useTable({ columns, data, defaultColumn }, useBlockLayout);

	initialState.hiddenColumns = ["cost_allocation_id", "cost_allocation_id"];

	return (
		<div className="max-w-full block mr-6">
			<div className="max-w-full block overflow-x-auto overflow-y-hidden">
				<div {...getTableProps()} className="w-full cursor-default">
					<div className="py-2 border-b-0 border-border pl-4 text-text-2 w-full">
						{headerGroups.map((headerGroup) => (
							<div {...headerGroup.getHeaderGroupProps()} className="tr">
							{headerGroup.headers.map((column) => (
								<div
									{...column.getHeaderProps()}
									className="th uppercase text-sm"
								>
									{column.render("Header")}
								</div>
							))}
							</div>
						))}
					</div>

					<div {...getTableBodyProps()} className="">
						{rows.map((row, index) => {
							prepareRow(row);

							return (
								<div
									{...row.getRowProps({
									})}
									className={`"tr border-b ${row.original.sub_organisation !== "" ? 'border-b-0' : 'border-t border-b border-border'}	`}
								>
									{row.cells.map((cell) => {
										return (
											<div
												{...cell.getCellProps()}
												className="td truncate pl-4 pt-4 pb-6 text-sm"
											>
												{cell.render("Cell")}
											</div>
										);
									})}
								</div>
							);
						})}
					</div>
				</div>
			</div>
		</div>
	);
}